/**
 * Copyright (C) 2017 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

import { LuiInputBase } from '@here/lui-base'
import scopedCss from 'lui-button.scss'
import template from 'lui-button.html'

export default class LuiButton extends LuiInputBase {
  constructor() {
    super()

    this.syncedProperties = ['disabled']
  }

  static get is() {
    return 'lui-button'
  }

  static get attributeToggles() {
    return ['disabled', 'iconposafter', 'icononly']
  }

  static get cssClassToggles() {
    return ['secondary']
  }

  static get clickTriggers() {
    return ['space', 'enter']
  }

  static get css() {
    return scopedCss
  }

  static get template() {
    return template
  }

  get spinner() {
    if (!this._spinner) {
      this._spinner = document.createElement('lui-spinner')
      this._spinner.setAttribute('paused', '')
      this.spinnerWrapper.appendChild(this._spinner)
    }
    return this._spinner
  }

  get spinnerWrapper() {
    if (!this._spinnerWrapper) {
      this._spinnerWrapper = document.createElement('div')
      this._spinnerWrapper.classList.add('lui-button__spinner-wrapper')
      this.shadowRoot.appendChild(this._spinnerWrapper)
    }
    return this._spinnerWrapper
  }

  get disabled() {
    return this.hasAttribute('disabled') || this.getAttribute('state') === 'processing'
  }

  set disabled(value) {
    value ? this.setAttribute('disabled', '') : this.removeAttribute('disabled')
  }

  get iconOnly() {
    return this.hasAttribute('icononly')
  }

  get iconPositionAfter() {
    return this.hasAttribute('iconposafter')
  }

  button(cb) {
    const button = this.querySelector('button')
    if (button) return cb(button)
  }

  disabledChanged() {
    super.disabledChanged()

    const _disabledChanged = () => {
      if (this.disabled) {
        this.button((button) => {
          this._ignoreChange = true
          button.disabled = true
          this._writeField(button)
        })
        if (this.seedElement) {
          this._toggleInteractivityClassnames()
        }
      } else {
        this._ignoreChange = true
        this.button((button) => {
          this._ignoreChange = true
          button.disabled = false
          this._writeField(button)
        })
        if (this.seedElement) {
          this._toggleInteractivityClassnames()
        }
      }
    }

    const _isSeedElementAvailable = () => {
      return this._initialized && !!this.seedElement
    }

    this.poll(_disabledChanged, _isSeedElementAvailable, 1000)
  }

  iconChanged(oldValue, newValue) {
    let element = this.shadowRoot.querySelector('lui-icon')
    if (newValue) {
      if (!element) {
        element = document.createElement('lui-icon')
      }
      element.setAttribute('icon', newValue)
      const span = this.shadowRoot.querySelector('span')
      if (this.iconPositionAfter) {
        span.parentNode.appendChild(element)
      } else {
        span.parentNode.insertBefore(element, span)
      }
    } else if (element) {
      this.seedElement.removeChild(element)
    }
  }

  init() {
    super.init()

    this._toggleInteractivityClassnames()
    this._setSkipButtonFocus()
  }

  onConnected() {
    super.onConnected()

    this._clickBound = this._clickBound || this._clickAction.bind(this)
    this.seedElement.addEventListener('click', this._clickBound)
    this.setAttribute('role', 'button')

    this.delay(() => {
      this._getField()

      if (this.querySelector('button')) {
        this.classList.add('has-slotted-button')
      } else {
        this.classList.remove('has-slotted-button')
      }
    })
  }

  onDisconnected() {
    super.onDisconnected()
    this.seedElement.removeEventListener('click', this._clickBound)
  }

  stateChanged(oldValue, newValue) {
    if (newValue === 'processing') {
      this.spinner.removeAttribute('paused')
    } else {
      this.spinner.setAttribute('paused', '')
    }
  }

  _addPropertyListenerToField(field) {
    this.syncedProperties.forEach((property) => {
      const nativeImplementation = Object.getOwnPropertyDescriptors(Object.getPrototypeOf(field))[property]
      if (!nativeImplementation) return
      Object.defineProperty(field, property, {
        set: (value) => {
          nativeImplementation.set.call(field, value)
          if (this._ignoreChange) return
          this._ignoreChange = true
          this._readField(field)
          this._ignoreChange = false
        },
        get: nativeImplementation.get,
      })
    })
  }

  _clickAction(event) {
    if (this.disabled || this.getAttribute('state') === 'processing') {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    if (this.getAttribute('type') === 'submit' && this.matches(`form ${this.constructor.is}`)) {
      let parentNode = this.parentNode
      let form = null
      while (parentNode) {
        if (parentNode.matches('form')) {
          form = parentNode
          break
        }
        parentNode = parentNode.parentNode
      }
      form.submit()
    }
  }

  _getField() {
    this._field = this.closest('a, button') || this.querySelector('button')
    this._ignoreChange = true

    if (this._field) {
      this.removeAttribute('tabindex')
      this._field.classList.add('lui-reset-styles')
      if (!this.fieldListenerInitialized) this._addPropertyListenerToField(this._field)
      this.fieldListenerInitialized = true
      this._readField(this._field)
    }
    this._ignoreChange = false
    return this._field || this
  }

  _readField(field) {
    field = field || this.field
    this.syncedProperties.forEach((property) => {
      const nativeImplementation = Object.getOwnPropertyDescriptors(Object.getPrototypeOf(field))[property]
      const value = nativeImplementation ? field[property] : field.getAttribute(property)
      if (value) {
        this.setAttribute(property, value === true ? '' : value)
      } else {
        this.removeAttribute(property)
      }
    })
  }

  _setSkipButtonFocus() {
    this.button((button) => {
      button.tabIndex = -1 // Only <lui-button> should be reachable by keyboard
    })
  }

  _toggleInteractivityClassnames() {
    const isDisabled = this.getAttribute('disabled') !== null
    this.seedElement.classList.toggle('lui-interactive', !isDisabled)
    this.seedElement.classList.toggle('lui-disabled', isDisabled)
  }

  _writeField(field) {
    field = field || this.field
    this.syncedProperties.forEach((property) => {
      field[property] = this[property]
    })
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-button/lui-button.js
// module id = 178
// module chunks = 0 1 18